// ITEM 2 — **THE PLUM WINDOW DOES NOT OPEN UNTIL THE CHART IS CAST AND STORED.**
//
// HER REPORT, off her own live walk: *Lala greeted her "I have your chart right here" WHILE a popup
// said the chart was still processing.* She was speaking chartless, and every word of that greeting
// was a claim about a thing that did not exist yet.
//
// >>> **MEASURED BEFORE A LINE WAS WRITTEN (R25/R180), AND THE CAUSE IS AN ORDER RATHER THAN A
// SENTENCE.** <<< `handleRoomCreate` answers `{ ok: true, generating: true, expected: [...] }`
// **IMMEDIATELY** and fires the two foundational readings **DETACHED** — N18-1's own ruling, and it
// is right, because two narrative engines take well past a safe HTTP timeout. The CAST happens
// inside that detached work. So `TalkCapture`'s `onArrive` fired on a room that existed and a chart
// that did not, and `handleLalaMessages` then composed the greeting on the next read with
// `lalaChartFacts` answering `missing: "no-chart"`.
//
// **AND THE GREETING'S FLOOR IS UNCONDITIONAL, WHICH IS WHY NOTHING WAS RED.** `composeOpening`
// always says `lala.opening.chartInHand` — *"I have your chart right here."* — and the only part
// that reads the chart is the optional BEAT, which correctly falls back to silence. So the beat did
// exactly what it was built to do and the sentence around it lied anyway (R38/R165's shape one
// clause down: the guarded half was guarded and the unguarded half is the one she read).
//
// >>> **THREE STATES, NEVER TWO, AND THE THIRD IS THE ONE A NAIVE BUILD LOSES (R18).** <<< A paying
// member starting a named conversation is **NOT ON THIS RAIL AT ALL** — she has had her chart for
// months and holding her page would be this round breaking a room that works. `not-funnel` is that
// state, and everything about it is byte-identical to what shipped (ITEM 12's neutrality argument).
//
// >>> **AND IT FAILS CLOSED, WHICH IS THE OPPOSITE DIRECTION FROM MOST OF THIS HOUSE AND IS SAID SO
// (R19/R8-4).** <<< An UNREADABLE chart read holds the page. Holding wrongly costs a stranger a few
// seconds of a wait state she was going to see anyway; opening wrongly is **the exact contradiction
// she reported** — the house telling her it is holding her chart while it is not — on the first
// screen her advertising ever pays for. One of those two is visible and recoverable and the other
// is not.
//
// It imports NOTHING, which is the entry requirement that lets a FAST fixture LOAD and RUN every
// outcome with no database, no network and no browser (R146: reach is transitive).

/** The three answers, declared rather than spelled at three call sites. */
export const CHART_GATE_STATES = ["open", "wait", "not-funnel"];

/** Why a chart is not ready. NAMED rather than collapsed into one word (R18): *it has not been cast
 *  yet* and *we could not ask* are different facts about the house, and only the first is a wait
 *  that ends by itself. Both HOLD the page; the receipt is what tells them apart afterwards. */
export const CHART_WAIT_REASONS = ["casting", "unreadable"];

/**
 * >>> **IS THERE REALLY A CAST CHART BEHIND THIS MEMBER?** <<<
 *
 * `facts` is `lalaChartFacts`' own answer — the SAME reader the greeting's beat consults, so there
 * is ONE answer to *does she have a chart* rather than a second opinion beside it (R28/R29), and
 * the copy that drifted would be the one deciding whether the house tells her the truth.
 *
 * A chart with no PLANETS in it is not a chart: `lalaChartFacts` answers `{ missing: "no-chart",
 * planets: [] }` for a member with nothing stored, and a row that somehow arrived with an empty
 * placement list can produce no true sentence either. Both are `casting`.
 */
export function chartReadyState(facts) {
  if (facts === null || facts === undefined) return { ready: false, why: "unreadable" };
  if (typeof facts !== "object") return { ready: false, why: "unreadable" };
  if (facts.missing) return { ready: false, why: "casting" };
  if (!Array.isArray(facts.planets) || !facts.planets.length) return { ready: false, why: "casting" };
  return { ready: true, why: "ready" };
}

/**
 * >>> **THE ONE GATE, READ BY THREE CONSUMERS.** <<<
 *
 *  - the GREETING composes only on `open`, so `"I have your chart right here"` is true when it is said;
 *  - a TURN is refused on `wait`, so no free turn can be spent before the chart exists — which is her
 *    *structurally* clause: the server refuses, rather than the client remembering not to ask;
 *  - the PAGE holds its wait state on `wait`.
 *
 * Three call sites each deciding this for themselves is three answers to one question, and the one
 * that drifts is whichever surface nobody re-walks.
 *
 * **PRESENCE, NEVER COERCION (R53/R136/R137).** Both flags arrive off a database read and a payload,
 * where `Number(true)` is 1 and `Boolean("false")` is `true` — and a coerced `granted` puts a paying
 * member on the funnel rail while a coerced `ready` opens the window on a chart that is not there.
 */
export function funnelChartGate({ granted, ready } = {}) {
  if (granted !== true) return "not-funnel";
  return ready === true ? "open" : "wait";
}

/** The greeting may be composed. Held as its own name because its third fact — an EMPTY thread — is
 *  FUNNEL-4's own clause and belongs beside the two this round adds (R18). */
export function mayGreet({ granted, ready, threadEmpty } = {}) {
  return threadEmpty === true && funnelChartGate({ granted, ready }) === "open";
}

/**
 * >>> **DOES HER PAGE HOLD? — AND *WE DID NOT LOOK* IS NOT *WE LOOKED AND COULD NOT READ IT*
 * (R18).** <<<
 *
 * MEASURED 2026-08-26, read-only, on the first day this gate had a receipt at all: **63 of 474
 * arrival reads answered `unreadable`, and not one of them was a chart that could not be read.**
 * All nine of the held members with a seat hold placements RIGHT NOW and seven of the nine had
 * already been greeted `with-beat`, which is a greeting composed FROM those placements. The two
 * corroborating receipts are silent — `living-room-fault`, which is the one row that carries a
 * pool-connect timeout, has **ZERO** rows on the same day, and the turn-refusal arm has fired
 * **ZERO** times all time.
 *
 * **THE CAUSE IS THAT THE READ WAS NEVER PERFORMED.** `handleLalaMessages` reads the chart only for
 * an EMPTY thread, because the greeting is the only consumer that needs it — so for a member coming
 * back to a conversation she already has, the facts are `null` **by construction**, and
 * `chartReadyState(null)` answers `unreadable`, correctly, about a question nobody asked. Every
 * returning funnel member then rode back `chartCasting: true` and wrote a receipt saying her chart
 * could not be read. **A receipt for something that did not happen** (R141), and it is what sent a
 * whole window hunting PERF-1's five-connection pool against a fault log with nothing in it.
 *
 * So the hold is scoped to the fact the read is scoped to. **A member with messages in her thread
 * has already arrived**, which is `mayGreet`'s own condition and the other half of the same
 * question: on an empty thread the gate is asked and `open` greets her while `wait` holds her; on a
 * thread that is not empty neither is asked, because there is nothing to decide.
 *
 * **HER CLAUSE 1 IS UNTOUCHED, AND THAT IS ARITHMETIC RATHER THAN CARE.** On the capture's own poll
 * a thread can become non-empty only by the greeting being written, and the greeting is written only
 * when the gate says `open` — so on the one path her clause is about, `threadEmpty === false`
 * already implies the chart was read and was ready. The hold cannot be weakened where it is load-
 * bearing.
 *
 * It DELEGATES to `funnelChartGate` and states no second opinion about what a wait is (R28/R29):
 * a second spelling here is a page that holds on a rule the greeting does not share.
 */
export function chartHoldsPage({ granted, ready, threadEmpty } = {}) {
  if (threadEmpty !== true) return false;
  return funnelChartGate({ granted, ready }) === "wait";
}


/** A turn may be spent. `not-funnel` passes UNTOUCHED — every paying member on this site is on the
 *  ordinary rail and this round may not cost her a single turn (M-neutrality). */
export function funnelTurnAllowed({ granted, ready } = {}) {
  return funnelChartGate({ granted, ready }) !== "wait";
}

/** Her wait copy, as catalog keys. The words are `strings.js`' in both languages, because this file
 *  imports nothing and importing `t` here would buy the translation and pay for it with the one
 *  property this module exists for (R211's ruling on `numerology-stages.ts`).
 *
 *  **HER CLAUSE: HOUSE VOICE, NEVER MACHINERY (S1-A/R117).** A stranger does not have a
 *  `subject_chart` row being cast, she has her chart being pulled. Nothing here names a table, a
 *  vendor, a job or a status.
 *
 *  >>> **AND IT IS ONE KEY RATHER THAN THREE, WHICH IS A MEASUREMENT RATHER THAN A TRIM.** <<< The
 *  first draft declared a `line` and an `under` for the wait state — and `TALK_STAGES` ALREADY
 *  carries that state's words, in her voice, rotating ("Finding your place on the sky", "Listening
 *  for what your chart wants said first"). A second wait line beside it is two answers to *what the
 *  wait says* (R28/R29), and ITEM 14b caught both rows as read by NOBODY in the round that wrote
 *  them. What is left is the one sentence nothing else composes: the SERVER's refusal. */
export const CHART_WAIT_KEYS = {
  refused: "talk.chartWait.refused", // what the server says if a turn is asked for too early
};

// ---------------------------------------------------------------------------------------------
// >>> **THE HOLD, AND ITS CEILING.** <<<
//
// Her clause 1: *submit -> the animated glowing "Pulling your chart..." state HOLDS THE PAGE -> only
// when the chart is ready does the window arrive.* The capture polls the answer the page was going
// to ask for anyway, so there is no second door and no second reader (R28/R29).
//
// **IT IS BOUNDED, AND THE CEILING IS NOT A WEAKENING OF HER LAW.** An unbounded hold is a glow
// forever, which is the failure `talkCaptureState`'s own note names one module over. Past the
// ceiling the page proceeds -- and it CANNOT reproduce her defect by doing so, because the SERVER
// gate is independent of this one: the greeting is withheld and a turn is refused whatever this
// client decides. That composition is the whole reason the law was put on the server as well.
// ---------------------------------------------------------------------------------------------

/** Every 1.5s. Slow enough that a hundred strangers do not hammer one read, fast enough that a chart
 *  which lands in two seconds does not cost her four. */
export const CHART_POLL_MS = 1_500;

/** Two minutes. `runRoomReport` fires the CAST and the narrative together and the cast is the fast
 *  half, so this is generous against the thing being waited for rather than against the whole job. */
export const CHART_POLL_CEILING_MS = 120_000;

/**
 * >>> **WHAT ONE TICK DECIDES (R18: THREE ANSWERS, NEVER TWO).** <<<
 *
 *  - `open`  — the answer landed and says the chart is real.
 *  - `wait`  — it is still casting, OR the read itself failed. **A read that did not land is not an
 *              answer** (R18/R65), and treating it as one opens the window on a chart nobody
 *              confirmed, which is her reported defect wearing a network blip.
 *  - `give-up` — past the ceiling. Named rather than folded into `open`, because *the chart is ready*
 *              and *we stopped waiting* are different facts and only one of them is a promise.
 */
export function chartPollVerdict({ answered, casting, elapsedMs, ceilingMs = CHART_POLL_CEILING_MS } = {}) {
  if (answered === true && casting !== true) return "open";
  const ms = typeof elapsedMs === "number" && Number.isFinite(elapsedMs) ? elapsedMs : 0;
  const cap = typeof ceilingMs === "number" && Number.isFinite(ceilingMs) && ceilingMs > 0
    ? ceilingMs : CHART_POLL_CEILING_MS;
  return ms >= cap ? "give-up" : "wait";
}
